import { brand, founders, capabilities, industries } from "@/data/content";

export const SITE = {
  url: "https://www.hashtagmarketing.in",
  name: "HashTag Marketing",
  title: "Integrated Marketing Agency in Gurgaon, Delhi NCR | HashTag Marketing",
  description:
    "HashTag Marketing is an independent integrated marketing agency in Gurgaon, Delhi NCR with 15+ years in brand strategy, creative, digital, PR, events and exhibitions for brands across India.",
  image: "/og-image.jpg",
  twitter: "@HashtagMkting",
  locale: "en_IN",
  foundingDate: "2013-08",
  geo: { lat: 28.4684, lng: 77.0813 },
  address: {
    street: "403, Gallaria Market, DLF City Phase IV",
    locality: "Gurugram",
    region: "Haryana",
    postalCode: "122002",
    country: "IN",
  },
  hours: { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "10:00", closes: "19:00" },
  keywords: [
    "integrated marketing agency Gurgaon",
    "integrated marketing agency Delhi NCR",
    "branding agency Gurgaon",
    "brand strategy agency Delhi NCR",
    "PR agency Delhi NCR",
    "experiential marketing agency India",
    "digital marketing agency Gurgaon",
    "creative agency Gurgaon",
    "event and brand activation agency Gurgaon",
    "exhibition design agency Delhi NCR",
    "marketing communications agency India",
    "brand launch agency India",
  ],
};

export const ORG_ID = `${SITE.url}/#organization`;
export const WEBSITE_ID = `${SITE.url}/#website`;
export const orgRef = { "@id": ORG_ID };

export const abs = (p) => (!p ? undefined : p.startsWith("http") ? p : `${SITE.url}${p}`);

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
export function monthToIso(label) {
  if (!label) return undefined;
  const [m, y] = label.toLowerCase().split(" ");
  const idx = MONTHS.indexOf(m);
  if (idx < 0 || !y) return undefined;
  return `${y}-${String(idx + 1).padStart(2, "0")}-01`;
}

export function webPageSchema({ path, title, description, type = "WebPage", image }) {
  return {
    "@type": type,
    "@id": `${abs(path)}#webpage`,
    url: abs(path),
    name: title,
    description,
    inLanguage: "en-IN",
    isPartOf: { "@id": WEBSITE_ID },
    about: orgRef,
    primaryImageOfPage: image ? { "@type": "ImageObject", url: abs(image) } : undefined,
  };
}

export function breadcrumbSchema(items) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.path),
    })),
  };
}

export function servicesSchema() {
  return {
    "@type": "ItemList",
    name: "Integrated marketing services by HashTag Marketing",
    itemListElement: capabilities.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Service",
        name: c.title,
        description: c.body,
        serviceType: c.points.map((p) => p.label),
        provider: orgRef,
        areaServed: ["Gurgaon", "Delhi NCR", "India"],
        url: `${SITE.url}/services`,
      },
    })),
  };
}

export function industriesSchema() {
  return {
    "@type": "ItemList",
    name: "Industries served by HashTag Marketing",
    itemListElement: industries.map((ind, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: ind.name,
      description: ind.blurb,
      url: `${SITE.url}/industries#${ind.slug}`,
    })),
  };
}

export function caseStudySchema(cs) {
  return {
    "@type": "CreativeWork",
    "@id": `${SITE.url}/work/${cs.slug}#work`,
    name: `${cs.client}: ${cs.title}`,
    headline: `${cs.client} ${cs.title}`,
    description: cs.excerpt,
    image: abs(cs.image),
    url: `${SITE.url}/work/${cs.slug}`,
    genre: cs.category,
    keywords: cs.services.join(", "),
    about: { "@type": "Organization", name: cs.client },
    creator: orgRef,
    publisher: orgRef,
    dateCreated: cs.year || undefined,
    inLanguage: "en-IN",
  };
}

export function articleSchema(post) {
  const iso = monthToIso(post.date);
  return {
    "@type": "Article",
    "@id": `${SITE.url}/insights/${post.slug}#article`,
    headline: post.title,
    description: post.dek || post.excerpt,
    image: abs(post.image),
    url: `${SITE.url}/insights/${post.slug}`,
    mainEntityOfPage: `${SITE.url}/insights/${post.slug}`,
    articleSection: post.category,
    author: { "@type": "Organization", name: post.author, "@id": ORG_ID },
    publisher: orgRef,
    datePublished: iso,
    dateModified: iso,
    inLanguage: "en-IN",
    wordCount: post.body?.reduce((n, b) => n + (b.text || b.items?.join(" ") || "").split(/\s+/).length, 0),
  };
}

export function foundersSchema() {
  return founders.map((f) => ({
    "@type": "Person",
    "@id": `${SITE.url}/about#${f.slug}`,
    name: f.name,
    jobTitle: f.role.split(" — ")[0],
    image: abs(f.image),
    description: f.bio,
    worksFor: orgRef,
    url: `${SITE.url}/about`,
  }));
}

export function contactSchema() {
  return {
    "@type": "ContactPoint",
    contactType: "sales",
    telephone: brand.phone,
    email: brand.email,
    areaServed: "IN",
    availableLanguage: ["English", "Hindi"],
  };
}
